'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { Button } from '@/src/shared/ui/button';
import { Input } from '@/src/shared/ui/input';
import { Label } from '@/src/shared/ui/label';

import { loginSchema } from '../model/schemas';
import type { LoginFormValues, LoginState } from '../model/types';
import { loginAction } from '../api/loginAction';
import { isRedirectError } from '../lib/isRedirectError';
import { GoogleSignInButton } from './GoogleSignInButton';
import {
  getEmailErrorMessage,
  getPasswordErrorMessage,
  getLoginApiErrorMessage,
} from './mapLoginError';
import { useOAuthFeedback } from './useOAuthFeedback';
import { useLoginNotice } from './useLoginNotice';

export function LoginForm() {
  const t = useTranslations('auth.login');
  const tSocial = useTranslations('auth.social');
  const params = useParams();
  const locale = (params.locale as string) ?? 'es';
  useOAuthFeedback();
  useLoginNotice();

  const [formState, setFormState] = useState<LoginState>('idle');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
  });

  const emailError = getEmailErrorMessage(t, errors.email?.message);
  const passwordError = getPasswordErrorMessage(t, errors.password?.message);

  async function onSubmit(data: LoginFormValues) {
    setFormState('submitting');
    try {
      const result = await loginAction(data, locale);
      if (result && !result.ok) {
        toast.error(getLoginApiErrorMessage(t, result.code));
        setFormState('error');
      }
    } catch (err) {
      // A successful login redirects, and Next signals that by throwing.
      if (isRedirectError(err)) throw err;
      toast.error(getLoginApiErrorMessage(t, 'server_error'));
      setFormState('error');
    }
  }

  const submitting = formState === 'submitting';

  return (
    <form
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-4"
    >
      <GoogleSignInButton />

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" aria-hidden />
        <span>{tSocial('divider')}</span>
        <span className="h-px flex-1 bg-border" aria-hidden />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="login-email" className="text-xs font-medium text-muted-foreground">
          {t('fields.email.label')}
        </Label>
        <Input
          id="login-email"
          type="email"
          autoComplete="username"
          placeholder={t('fields.email.placeholder')}
          aria-invalid={!!emailError}
          aria-describedby={emailError ? 'login-email-error' : undefined}
          className="h-11 border-border/80 bg-background"
          {...register('email')}
        />
        {emailError && (
          <p id="login-email-error" role="alert" className="text-xs text-destructive">
            {emailError}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="login-password" className="text-xs font-medium text-muted-foreground">
          {t('fields.password.label')}
        </Label>
        <Input
          id="login-password"
          type="password"
          autoComplete="current-password"
          placeholder={t('fields.password.placeholder')}
          aria-invalid={!!passwordError}
          aria-describedby={passwordError ? 'login-password-error' : undefined}
          className="h-11 border-border/80 bg-background"
          {...register('password')}
        />
        {passwordError && (
          <p id="login-password-error" role="alert" className="text-xs text-destructive">
            {passwordError}
          </p>
        )}
      </div>

      <Button type="submit" disabled={submitting} className="h-11 w-full rounded-lg text-[14px]">
        {submitting ? (
          <>
            <Loader2 className="animate-spin" />
            {t('submitting')}
          </>
        ) : (
          t('submit')
        )}
      </Button>

      <div className="flex flex-col gap-2 text-center text-xs text-muted-foreground">
        <Link
          href={`/${locale}/register`}
          className="hover:text-foreground underline-offset-4 hover:underline"
        >
          {t('links.register')}
        </Link>
        <Link
          href={`/${locale}/forgot-password`}
          className="hover:text-foreground underline-offset-4 hover:underline"
        >
          {t('links.forgot_password')}
        </Link>
      </div>
    </form>
  );
}
